import React, { FunctionComponent, useEffect, useState } from 'react';
import Button from 'components/Button';
import classnames from 'classnames';
import { path } from 'ramda';
import { Grid } from 'semantic-ui-react';

import FormSelectList from 'components/FormComponents/FormSelect';
import ThrottleDetails from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/ThrottleDetails/ThrottleDetails';

import { useMerchantOffers } from 'helpers/useMerchantOffers';
import { useMerchants } from 'hooks/useMerchants';
import { useThrottle } from 'helpers/useThrottle';
import {
  TargetingRuleType,
  TargetingRule,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';

import { ClassProp } from 'helpers/types';
import { Theme } from 'config';
import withStyles from 'react-jss';

import { DataByKey } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.helpers';

const styles = (theme: Theme) => ({
  label: {
    borderLeft: `0.4rem solid ${theme.violet}`,
    padding: '.5rem',
  },
});

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  setTargetingRule: React.Dispatch<
    React.SetStateAction<TargetingRule | undefined>
  >;
  targetingRule?: TargetingRule;
};

const ThrottleSelection: FunctionComponent<Props> = ({
  setTargetingRule,
  targetingRule,
  classes,
}) => {
  const [dataByKey, setDataByKey] = useState<DataByKey>();
  const [useExistingThrottle, setUseExistingThrottle] = useState(false);
  const [merchantUuid, setMerchantUuid] = useState<string>();
  const {
    merchantOffers,
    getOfferByUuid,
    mechantOffersLoading,
    offersByUuid,
  } = useMerchantOffers(
    merchantUuid ?? '',
    TargetingRuleType.OFFER_THROTTLE_V1,
  );

  const { merchantDropdownOptions, merchantsLoading } = useMerchants();
  const {
    throttleOptions,
    getThrottleByUuid,
    getThrottleByUuidLoading,
    throttle,
  } = useThrottle();

  useEffect(() => {
    if (merchantUuid) {
      getOfferByUuid({
        variables: {
          params: { merchantUuid },
        },
      });
    }
  }, [merchantUuid]);

  useEffect(() => {
    const uuid = path(['OFFER_THROTTLE_UUID', 0], dataByKey);
    if (uuid) {
      getThrottleByUuid({
        variables: {
          params: { uuid },
        },
      });
    }
  }, [dataByKey]);

  const onSelectedMerchantChange = (merchantUuid: string) => {
    setMerchantUuid(merchantUuid);
  };

  const onOfferSelection = (offerUuid: string) => {
    const offer = offersByUuid.get(offerUuid);
    const offerSegments = offer?.segments;
    offerSegments?.forEach((segment) => {
      const rules = segment.targetingRules.filter(
        (rule) =>
          rule.type === TargetingRuleType.OFFER_THROTTLE_V1 && rule.dataByKey,
      );
      if (rules.length === 1) {
        setDataByKey(rules[0].dataByKey);
      }
    });
    setTargetingRule(undefined);
  };

  const selectExistingThrottle = () =>
    setUseExistingThrottle(!useExistingThrottle);

  const onThrottleSelection = () => {
    const uuid = path(['OFFER_THROTTLE_UUID', 0], dataByKey) as string;
    const hashmod_salt = path(['HASHMOD_SALT', 0], dataByKey) as string;
    if (uuid && hashmod_salt && dataByKey) {
      const rule: TargetingRule = {
        type: TargetingRuleType.OFFER_THROTTLE_V1,
        data: [hashmod_salt, uuid],
        dataByKey,
      };
      setTargetingRule(rule);
    }
  };

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column width={16}>
          <div className={classnames(classes.label)}>
            <h3>Throttle</h3>
          </div>
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={14}>
          <Button
            title="Select existing Throttle"
            onClick={selectExistingThrottle}
            className={classnames({ selected: useExistingThrottle })}
          />
        </Grid.Column>
      </Grid.Row>
      {useExistingThrottle && (
        <Grid.Row>
          <Grid.Column width={4}>
            <FormSelectList
              label="Merchants"
              loading={merchantsLoading}
              dropDownOptions={merchantDropdownOptions}
              search
              onSelectChange={onSelectedMerchantChange}
              name="merchant"
            />
          </Grid.Column>
          <Grid.Column width={4}>
            <FormSelectList
              loading={mechantOffersLoading}
              disabled={merchantsLoading || mechantOffersLoading}
              label="Offers"
              name="merchantOffers"
              onSelectChange={onOfferSelection}
              dropDownOptions={merchantOffers}
            />
          </Grid.Column>
          <Grid.Column width={6}>
            <FormSelectList
              disabled={
                merchantsLoading ||
                mechantOffersLoading ||
                getThrottleByUuidLoading
              }
              loading={getThrottleByUuidLoading}
              label="Throttle Details"
              name="throttleDetails"
              defaultValue={throttle ? throttle.name : ' '}
              dropDownOptions={throttleOptions}
              onSelectChange={onThrottleSelection}
            />
          </Grid.Column>
        </Grid.Row>
      )}
      {targetingRule && throttle && (
        <Grid.Row>
          <ThrottleDetails throttle={throttle} />
        </Grid.Row>
      )}
    </Grid>
  );
};

export default withStyles(styles)(ThrottleSelection);
